<template>
  <div class="alert alert-danger" role="alert" v-if="orgInfo && !orgInfo.connectedToStripe">
    Please set up your Stripe account so that participants can register and pay for your {{terms?.ProgramPlural.toLowerCase()}}.
    {{terms?.ProgramPlural}} with a cost attached will not show on the website until your Stripe account has been set up.
    Go to {{ setting.platformShortName }} > Settings > Stripe
  </div>
  <div class="right-panel">
    <div class="row">
      <div class="col">
        <h4>{{terms?.ProgramPlural}}</h4>
      </div>
      <div class="col text-right">
        <button class="btn btn-primary" @click="onAddClick">
          Create a {{terms?.Program}}/Session
        </button>
      </div>
    </div>

    <div class="d-flex flex-row content form">
      <div class="row form-input mr-1" v-if="orgInfo && orgInfo.associationId">
        <label for="venue" class="form-label">Search by Venue <span>*</span></label>
        <div class="input-group">
          <select class="form-select" id="venue" v-model="selectedVenueId" :disabled="isLoadingVenueSelector">
            <option :value="undefined" v-if="!setting.loadAllProgramsForAssociationUser">-- Select One --</option> 
            <option :value="undefined" v-if="setting.loadAllProgramsForAssociationUser">-- All --</option> 
            <option v-for="item in venuesSelector" :value="item.key">
              {{ item.value }}
            </option>
          </select>
        </div>
      </div>
      <div class="row mr-1 form-input">
        <label for="basic-url">Search By Category</label>
        <div class="input-group">
          <select class="form-select" v-model="searchByCategory" id="category" @change="onCategoryChange">
            <option :value="undefined">-- All --</option>
            <option v-for="(category, index) in categories" :value="category.id" :key="index">
              {{ category.description }}
            </option>
          </select>
        </div>
      </div>
      <div class="row mr-1 form-input">
        <label for="basic-url">Search by School Term</label>
        <div class="input-group">
          <select class="form-select" v-model="searchByTerm" id="category"
            :disabled="searchByCategory == 2 || !searchByCategory">
            <option :value="undefined">-- All --</option>
            <option v-for="(term, index) in searchableProgramTerms" :value="term.programTermId" :key="index">
              {{ term.programTermName }}
            </option>
          </select>
        </div>
      </div>
      <div class="row mr-1 form-input">
        <label for="basic-url">
          <label class="mr-2 ">Search by Date</label>

          <input class="form-check-input mr-1" type="radio" name="searchByDateRadio" v-model="searchByDateRadio"
            value="1" id="searchByDateRadio1" />
          <label class="form-check-label search-by-date mr-2" for="searchByDateRadio1"> Current & Future </label>

          <input class="form-check-input mr-1" type="radio" name="searchByDateRadio" v-model="searchByDateRadio"
            value="2" id="searchByDateRadio2" />
          <label class="form-check-label search-by-date mr-2" for="searchByDateRadio2"> Past </label>

          <input class="form-check-input mr-1" type="radio" name="searchByDateRadio" v-model="searchByDateRadio"
            value="3" id="searchByDateRadio3" />
          <label class="form-check-label search-by-date" for="searchByDateRadio3"> Date Range </label>
        </label>
        <div class="input-group">
          <VueDatePicker v-model="searchByDate" range format="dd/MM/yyyy" :clearable="true" :enable-time-picker="false"
            :disabled="searchByDateRadio != '3'" />
        </div>
      </div>
      <div class="row form-input">
        <label for="basic-url">Search by {{terms?.Program}} Name</label>
        <div class="input-group">
          <input type="text" maxlength="100" v-model="searchByProgramName" class="form-control"
            aria-label="Program Name" @input="searchByProgramNameDebounce" />
        </div>
      </div>
    </div>

    <div class="table-wrapper">
      <EasyDataTable v-if="items" class="data-table" v-model:server-options="serverOptions" table-class-name="datatable"
        :headers="programsTableHeaders" :items="items" :server-items-length="totalCount" :loading="isLoadingPrograms"
        buttons-pagination @expand-row="loadProgramOnExpand" must-sort :alternating="true">
        <template #item-startDate="item">
          {{ moment(item.startDate).format("DD MMM YYYY") }} -
          {{ moment(item.endDate).format("DD MMM YYYY") }}
        </template>
        <template #item-category="item">
          {{ item.programCategoryName }}
        </template>
        <template #item-programName="item">
          <a :href="`${publicWebsiteUrl}${item.programCategoryPage}?programUID=${item.programUID}`" target="_blank">{{
            item.programName
          }}</a>
          <p v-if="item.runBy"><small class="text-danger opacity-75">Run by {{ item.runBy }}</small></p>
        </template>
        <template #item-registrationCutOffDateUtc="item">
          {{ toZonedTime(item.registrationCutOffDateUTC) }}
        </template>
        <template #item-days="item">
          <div>{{ parseDay(item) }}</div>
          <div>
            {{
              moment(new Date().toDateString() + " " + item.sessionTimeFrom).format(
                "h:mm a"
              )
            }}
            -
            {{
              moment(new Date().toDateString() + " " + item.sessionTimeTo).format(
                "h:mm a"
              )
            }}
          </div>
        </template>
        <template #item-sessions="item">
          {{ item.sessions }}
        </template>
        <template #item-cost="item">
          {{ `${currency}${(Math.round(item.cost * 100) / 100).toFixed(2)}` }}
        </template>
        <template #item-registeredParticipants="item">
          <span v-if="isAccessToProgramChangesDisabled(item)">
            {{ `${item.registeredParticipants ?? 0}/${item.participantMaxCount ?? 0}` }}
          </span>
          <a v-else href="#" @click="() => viewParticipants(item.programUID)" >{{
            `${item.registeredParticipants ?? 0}/${item.participantMaxCount ?? 0}`
          }}</a>
        </template>
        <template #item-buttons="item">
          <div class="icon-col">
            <button class="icon-email" :disabled="isAccessToProgramChangesDisabled(item)" @click="() => emailParticipants(item)">
              <i class="bi bi-envelope"></i>
            </button>
            <button class="icon-edit" :disabled="isAccessToProgramChangesDisabled(item)" @click="() => editProgram(item, 0)" title="Edit">
              <i class="bi bi-pencil-square"></i>
            </button>
            <button class="icon-copy" :disabled="isAccessToProgramChangesDisabled(item)" @click="() => editProgram(item, 1)" title="Copy">
              <i class="bi bi-files"></i>
            </button>
            <button class="icon-delete" v-b-modal="'my-modal'" :disabled="item.registeredParticipants > 0 || isAccessToProgramChangesDisabled(item)"
              @click="() => deleteProgram(item)" :title="item.registeredParticipants > 0
                ? `Please note you can\'t delete a ${terms?.Program.toLowerCase()} with participants listed. Individual participants should be removed before attempting this action. Please note any refunds associated with deleting participants are the responsibility of the facility.`
                : 'Delete'
                ">
              <i :disabled="item.registeredParticipants > 0" class="bi bi-trash ms-1"></i>
            </button>
          </div>
        </template>
        <template #expand="item">
          <div class="row form-input" v-if="item.isLoadingProgram && !item.extra">
            <div class="col-sm-12 text-center">
              <div class="spinner-grow spinner-grow-sm" role="status"></div>
            </div>
          </div>
          <div v-else class="pa-4">

            <!-- Expanded view section Program Details -->
            <div class="row">
              <div class="row pb-4">
                <div class="col">
                  <b>{{terms?.Program}} Description: </b><br /><span class="render-white-space">{{
                    item.extra.programDescription
                  }}</span>
                </div>
                <div class="col">
                  <b>{{terms?.Program}} Types: </b>
                  <div v-for="type in item.extra.programTypes" class="badge rounded-pill bg-success mr-1">
                    {{
                      item.programCategoryId == 1
                        ? type.programTypeName.replace("Female", "Girls")
                        : item.programCategoryId == 2
                          ? type.programTypeName.replace("Female", "Women")
                          : type.programTypeName
                    }}
                  </div>
                </div>
                <div class="col" v-if="item.programCategoryId == 1">
                  <b>{{terms?.Program}} Term: </b>
                  <span>{{ item.extra.term }}</span>
                </div>
                <div class="col" v-if="item.programCategoryId == 2">&nbsp;</div>
              </div>
              <div class="row pb-4">
                <!-- <div class="col">
                <b>Maximum number of participants: </b> {{ item.extra.participantMaxCount }}
              </div> -->
                <div class="col">
                  <b>Priority Registration: </b>
                  {{ item.extra.hasPriorityRegistration ? "Yes" : "No" }}<span
                    v-if="item.extra.hasPriorityRegistration">{{
                      `, until ${item.extra.priorityRegistrationExpiryDate
                        ? moment(item.extra.priorityRegistrationExpiryDate).format(
                          "DD MMM yyyy"
                        )
                        : ""
                      } `
                    }}</span>
                  <div>
                    <button v-if="item.extra.priorityRegistrationCode && isPriorityNotExpired(item.extra)"
                      @click="() => copyLink(item.extra)" class="btn btn-xs btn-primary">
                      Copy link
                    </button>
                  </div>
                </div>
                <div class="col">
                  <b>Days: </b>
                  <div v-for="day in item.extra.days" class="badge rounded-pill bg-success mr-1">
                    {{ day }}
                  </div>
                </div>
                <div class="col"></div>
              </div>

              <div class="row pb-4">
                <div class="col" v-if="item.programCategoryId == 1">
                  <b>Enabled discounts for multiple participants: </b>
                  {{ item.extra.programCostSubsequentParticipant ? "Yes" : "No" }}
                  <span v-if="item.extra.programCostSubsequentParticipant">{{
                    `(${currency}${item.extra.programCostSubsequentParticipant
                      ? item.extra.programCostSubsequentParticipant
                      : ""
                    } discounted cost)`
                  }}</span>
                </div>
                <div class="col">
                  <b>Pro rata: </b> {{ item.extra.isProRata ? "Yes" : "No" }}
                </div>
                <div class="col"></div>
              </div>
            </div>

            <!-- Expanded view section: Coordinators and Deliverers -->
            <div class="row">
              <div class="col">
                <h4>Coordinators</h4>
                <EasyDataTable class="data-table" v-if="item && item.extra" v-model:server-options="staticServerOptions"
                  table-class-name="datatable inner" :headers="coordinatorsHeaders" :items="item.extra.coordinators"
                  hide-footer must-sort>
                  <template #item-Name="dataItem">
                    {{ dataItem.name }}
                  </template>
                  <template #item-email="dataItem">
                    {{ dataItem.email }}
                  </template>
                  <template #item-phone="dataItem">
                    {{ dataItem.phone }}
                  </template>
                </EasyDataTable>
              </div>
              <div class="col">
                <h4>Deliverers</h4>
                <EasyDataTable class="data-table" v-if="item && item.extra" v-model:server-options="staticServerOptions"
                  table-class-name="datatable inner" :headers="delivererHeaders" :items="item.extra.deliverers"
                  hide-footer :alternating="true" must-sort>
                  <template #item-Name="dataItem">
                    {{ dataItem.name }}
                  </template>
                  <template #item-email="dataItem">
                    {{ dataItem.membershipId ?? dataItem.email }}
                  </template>
                  <template #item-delivererType="dataItem">
                    {{ dataItem.delivererType }}
                  </template>
                </EasyDataTable>
              </div>
            </div>

            <!-- Expanded view section: Sessions and Program Vouchers -->
            <div class="row">
              <div class="col">
                <h4>Sessions</h4>
                <EasyDataTable class="data-table" v-if="item.extra && item.extra.programSessionsDisplay.length > 0"
                  v-model:server-options="serverOptions" table-class-name="datatable" :headers="programSessionHeaders"
                  :items="item.extra.programSessionsDisplay"
                  :server-items-length="item.extra.programSessionsDisplay?.length" hide-footer :alternating="true"
                  must-sort>
                  <template #item-date="item">
                    {{ item.date }}
                  </template>
                  <template #item-time="item">
                    {{ item.time }}
                  </template>
                </EasyDataTable>
              </div>
              <div class="col">
                <div class="row">
                  <div class="col">
                    <h4>{{terms?.Program}} Vouchers</h4>
                  </div>
                  <div class="col text-right">
                    <button class="btn btn-xs btn-primary" @click="redirectAndOpenCreateVoucherModal" :disabled="isAccessToProgramChangesDisabled(item)">
                      Create New Voucher
                    </button>
                  </div>
                </div>

                <EasyDataTable class="data-table" v-if="item && item.extra" v-model:server-options="staticServerOptions"
                  table-class-name="datatable inner" :headers="programVoucherCodeHeaders"
                  :items="item.extra.programVouchers" hide-footer :alternating="true" must-sort>
                  <template #item-voucherCode="dataItem">
                    {{ dataItem.voucherCode }}
                  </template>
                  <template #item-amount="dataItem">
                    <span v-if="dataItem.amount > 0 || (dataItem.amount == 0 && dataItem.discountPercentage == 0)">{{
                      currency + parseFloat(dataItem.amount).toFixed(2) }}</span>
                    <span v-else>{{ dataItem.discountPercentage + '%' }}</span>
                  </template>
                  <template #item-redemptionStartDate="dataItem">
                    {{ moment(dataItem.redemptionStartDate).format('DD MMM YYYY') + " - " +
                      moment(dataItem.redemptionEndDate).format('DD MMM YYYY') }}
                  </template>
                  <template #item-maxUsage="dataItem">
                    {{ dataItem.maxUsage }}
                  </template>
                  <template #item-actions="dataItem">
                    <a target="_blank"
                      :href="`${adminAppUrl}vouchers/${dataItem.programUID}/${dataItem.voucherCode}`"><i
                        class="bi bi-pencil-square action-icon"></i></a>
                  </template>
                </EasyDataTable>
              </div>
            </div>

          </div>
        </template>
      </EasyDataTable>
    </div>
  </div>
  <Modal title="Remove program" ref="deleteModal">
    <template #body>Are you sure you want to remove
      <strong>{{ `${selectedItem?.programName}` }}</strong>?</template>
    <template #footer>
      <button class="btn btn-danger" @click="onClickRemove">Remove</button>
    </template>
  </Modal>
  <Notification ref="notificationMsg" colorScheme="text-bg-success">
    <template #body>Successfully removed
      <strong>{{ `${selectedItem?.programName}` }}</strong></template>
  </Notification>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, onUnmounted } from "vue";
import type { Item, ServerOptions } from "vue3-easy-data-table";
import { storeToRefs } from "pinia";
import moment from "moment";
import { useProgramStore } from "@/stores/program-store";
import { useProgramTermStore } from "@/stores/terms-store";
import { useProgramCategoriesStore } from "@/stores/program-categories-store";
import Modal from "@/components/common/Modal.vue";
import Notification from "@/components/common/Notification.vue";
import { debounce } from "lodash";
import router from "@/router";
import { useProgramTypeStore } from "@/stores/program-type-store";
import { useCurrencyStore } from "@/stores/currency-store";
import { useAuthStore } from "@/stores/auth-store";
import { useVenueStore } from "@/stores/venue-store";
import { useRoute } from "vue-router";
import { useTermsStore } from "@/stores/language-terms";
import { useSettingStore } from "@/stores/setting-store";
import { utcToZonedTime } from "date-fns-tz";
import { format } from "date-fns";

const { terms } = storeToRefs(useTermsStore());
const { setting } = storeToRefs(useSettingStore());

const { venuesSelector, isLoadingVenueSelector } = storeToRefs(useVenueStore());

const route = useRoute();
const { getLoginOrgInfo } = useAuthStore();
const { loadProgramTerms } = useProgramTermStore();
const { getDefaultCurrency } = useCurrencyStore();
const { loadProgramCategories } = useProgramCategoriesStore();
const {
  getProgramById,
  getPrograms,
  removeProgram,
  getProgramTableHeaders,
  getPublicWebsiteUrl,
} = useProgramStore();
const { loadProgramTypes } = useProgramTypeStore();
const { orgInfo, isLoadingLogin, isNationalAssociationUser, isClubhouseUser } = storeToRefs(useAuthStore());
const { programTypes } = storeToRefs(useProgramTypeStore());
const { currency } = storeToRefs(useCurrencyStore());
const { getVenuesSelector } = useVenueStore();
const {
  programData,
  programs,
  totalCount,
  isLoadingPrograms,
  programsTableHeaders,
  publicWebsiteUrl,
} = storeToRefs(useProgramStore());
const { categories } = storeToRefs(useProgramCategoriesStore());
const { programTerms } = storeToRefs(useProgramTermStore());

const searchableProgramTerms = computed(() =>
  programTerms.value.filter(term => !term.isLegacy || isClubhouseUser.value)
);

const props = defineProps<{
  adminAppUrl: string;
  clubId: number;
}>();

const selectedVenueId = ref<number | undefined>(undefined);
let deleteModal = ref<any | null>(null);
let notificationMsg = ref<any | null>(null);

const calculateDefaultDate = (): Array<any> => {
  let date = moment();
  const startDate = date.add(-7, "d").toDate();
  const endDate = date.add(30, "d").toDate();
  return [startDate, endDate];
};

const searchByDate = ref<Date[]>();
const searchByProgramName = ref();
const searchByCategory = ref();
const searchByTerm = ref();
const selectedItem = ref();
const searchByDateRadio = ref();
const items = ref<Item[]>();

const defaultOptions = {
  page: 1,
  rowsPerPage: 20,
  sortBy: "startDate",
  sortType: "asc",
} as ServerOptions;

const serverOptions = ref<ServerOptions>(defaultOptions);

const staticServerOptions = ref<ServerOptions>({
  page: 1,
  rowsPerPage: 20,
});

const delivererHeaders = [
  { text: "Name", value: "name", sortable: false },
  { text: "Email / Membership ID", value: "email", sortable: false },
  { text: "Deliverer Qualification", value: "delivererType", sortable: false },
];

const coordinatorsHeaders = [
  { text: "Name", value: "name", sortable: false },
  { text: "Email", value: "email", sortable: false },
  { text: "Phone/Mobile", value: "phone", sortable: false },
];

const programSessionHeaders = [
  { text: "Session Date", value: "date", sortable: false },
  { text: "Session Time", value: "time", sortable: false },
];

const programVoucherCodeHeaders = [
  { text: "Voucher Code", value: "voucherCode", sortable: false },
  { text: "Discount", value: "amount", sortable: false },
  { text: "Validity", value: "redemptionStartDate", sortable: false },
  { text: "Maximum Usage", value: "maxUsage", sortable: false },
  { text: "", value: "actions", width: 30 }
];

const searchByProgramNameDebounce = debounce(($event) => {
  if ($event.target.value && $event.target.value.length <= 2) return;
  searchByProgramName.value = $event.target.value;
}, 600);

const isAccessToProgramChangesDisabled = (item: any) => {
  // If user is virtual club, and owns the program, allow access
  if (orgInfo.value?.isVirtualClub && orgInfo.value?.clubId === item.createdByClubId as number) return false;
  
  // If clubhouse user or regional association, and not a virtual club, and no createdByClubId, allow access
  if ((isClubhouseUser.value || !isNationalAssociationUser.value) && !orgInfo.value?.isVirtualClub && !item.createdByClubId) return false;

  // If national association user allow access
  if (isNationalAssociationUser.value) return false;

  return true;
};

const editProgram = async (item: any, isDuplicate: number) => {
  await router.push({
    name: "ParticipationProgram",
    params: {
      programUID: item.programUID,
      d: isDuplicate,
    },
    query: getParams(),
  });
};

const onAddClick = async () => {
  await router.push({
    name: "ParticipationProgram",
    query: getParams(),
  });
};

const parseQueryParams = () => {
  searchByDate.value = [];
  if (route.query && route.query.start) {
    searchByDate.value.push(moment(route.query.start as any).toDate())
  }
  if (route.query && route.query.end) {
    searchByDate.value.push(moment(route.query.end as any).toDate())
  }

  if (route.query && route.query.category) {
    searchByCategory.value = route.query.category
  }

  if (route.query && route.query.venueId) {
    selectedVenueId.value = parseInt(route.query.venueId as any)
  }

  if (route.query && route.query.termId) {
    searchByTerm.value = parseInt(route.query.termId as any)
  }

  if (route.query && route.query.programName) {
    searchByProgramName.value = route.query.programName as any
  }

  searchByDateRadio.value = '1';
  if (route.query && route.query.dateBy) {
    searchByDateRadio.value = route.query.dateBy as any
  }

  if (route.query && route.query.page) {
    serverOptions.value.page = parseInt(route.query.page as any)
  }
}

const parseDay = (item: any) => {
  var days = Array<string>();

  if (item.isMonday) {
    days.push("Monday");
  }

  if (item.isTuesday) {
    days.push("Tuesday");
  }

  if (item.isWednesday) {
    days.push("Wednesday");
  }

  if (item.isThursday) {
    days.push("Thursday");
  }

  if (item.isFriday) {
    days.push("Friday");
  }

  if (item.isSaturday) {
    days.push("Saturday");
  }

  if (item.isSunday) {
    days.push("Sunday");
  }

  return days.join(", ");
};

onMounted(async () => {

  parseQueryParams()

  await getLoginOrgInfo();
  await getProgramTableHeaders();
  await loadProgramCategories();
  await loadProgramTerms();
  await loadData();
  await loadProgramTypes();
  await getDefaultCurrency();

  if (orgInfo.value && orgInfo.value.associationId) {
    await getVenuesSelector(true);
  }
});

onUnmounted(async ()  => {
  programs.value = [];
})

const loadData = async () => {  
  if(isLoadingLogin.value || 
    (!isClubhouseUser.value && !setting.value.loadAllProgramsForAssociationUser && !selectedVenueId.value)
  ) return;

  //only change the sorting for "Current & Future" and "Past" options
  if(searchByDateRadio.value == '1' || searchByDateRadio.value == '2') {
    if(serverOptions.value.sortType === defaultOptions.sortType)
    {
      serverOptions.value.sortType ='asc';
    } else {
      serverOptions.value.sortType ='desc';
    }
  }
  
  await getPrograms(
    searchByProgramName.value,
    searchByDate.value && searchByDate.value.length > 0
      ? moment(searchByDate.value[0]).format("YYYY-MM-DD")
      : undefined,
    searchByDate.value && searchByDate.value.length > 1 && searchByDate.value[1]
      ? moment(searchByDate.value[1]).format("YYYY-MM-DD")
      : undefined,
    searchByTerm.value,
    searchByCategory.value,
    serverOptions.value.page,
    serverOptions.value.rowsPerPage,
    serverOptions.value.sortBy,
    serverOptions.value.sortType,
    selectedVenueId.value,
    searchByDateRadio.value,
  );
  items.value = programs.value;
};

const selectedProgramId = ref();
const loadProgramOnExpand = async (index: number) => {
  const currentPageIndex = index - ((serverOptions.value.page - 1) * serverOptions.value.rowsPerPage);
  let program = items.value && items.value[currentPageIndex];
  if (program) {
    selectedProgramId.value = program.programUID;
    program.isLoadingProgram = true;
    await getProgramById(program.programUID);


    program.isLoadingProgram = false;
    program.extra = Object.assign({}, programData.value);
    program.extra.programTypes = programTypes.value.filter(
      (x) =>
        program && program.extra.programTypesIds.some((p: number) => p == x.programTypeId)
    );
    program.extra.programTypes = programTypes.value.filter(
      (x) =>
        program && program.extra.programTypesIds.some((p: number) => p == x.programTypeId)
    );
    program.extra.term = programTerms.value.find(
      (x) => x.programTermId == program?.programTermId
    )?.programTermName;

    program.extra.days = [];
    if (programData.value.isMonday) {
      program.extra.days.push("Monday");
    }
    if (programData.value.isTuesday) {
      program.extra.days.push("Tuesday");
    }
    if (programData.value.isWednesday) {
      program.extra.days.push("Wednesday");
    }
    if (programData.value.isThursday) {
      program.extra.days.push("Thursday");
    }
    if (programData.value.isFriday) {
      program.extra.days.push("Friday");
    }
    if (programData.value.isSaturday) {
      program.extra.days.push("Saturday");
    }
    if (programData.value.isSunday) {
      program.extra.days.push("Sunday");
    }

    program = Object.assign({}, program);
  }
};

// redirect to Admin App and open create voucher modal
const redirectAndOpenCreateVoucherModal = () => {
  window.location.href = `${props.adminAppUrl}vouchers/${selectedProgramId.value}`
}

const onClickRemove = async () => {
  const result = await removeProgram(selectedItem.value.programUID);
  if (result) {
    deleteModal?.value?.hide();
    notificationMsg?.value?.show();
    await loadData();
  }
};

const deleteProgram = (item: any) => {
  selectedItem.value = Object.assign({}, item);
  if (deleteModal.value) {
    deleteModal.value.show();
  }
};

const isPriorityNotExpired = (program: any) => {
  if (!program?.hasPriorityRegistration || !program?.priorityRegistrationExpiryDate) {
    return false;
  }

  // Stored expiry is UTC (e.g. 13:59:59Z == end-of-day AEST), so compare exact UTC time.
  return moment.utc().isSameOrBefore(moment.utc(program.priorityRegistrationExpiryDate));
};

const getParams = () => {
  const url = new URL(location.href);

  const buildParams = {
    authNonce: url.searchParams.get("authNonce"),  // TEMP: Review how we can avoid using this query parameter
  } as any;

  if (searchByDate.value && searchByDate.value.length > 0) {
    buildParams.start = moment(searchByDate.value[0]).format("YYYY-MM-DD");
    if (searchByDate.value.length > 1) {
      buildParams.end = moment(searchByDate.value[1]).format("YYYY-MM-DD");
    }
  }

  if (searchByCategory.value) {
    buildParams.category = searchByCategory.value
  }

  if (selectedVenueId.value) {
    buildParams.venueId = selectedVenueId.value
  }

  if (searchByTerm.value) {
    buildParams.termId = searchByTerm.value
  }

  if (searchByProgramName.value) {
    buildParams.programName = searchByProgramName.value
  }

  if (searchByDateRadio.value) {
    buildParams.dateBy = searchByDateRadio.value
  }

  if (serverOptions.value.page > 1) {
    buildParams.page = serverOptions.value.page
  }

  return buildParams
}

const viewParticipants = async (programUID: string) => {
  await router.push({
    name: "ParticipationProgramParticipants",
    params: {
      programUID: programUID,
    },
    query: getParams(),
  });
};

const emailParticipants = async (item: any) => {
  await router.push({
    name: "ParticipationProgramEmail",
    params: {
      programUID: item.programUID,
      programName: item.programName,
    },
    query: getParams(),
  });
};

const copyLink = (data: any) => {
  const fallbackCodeQuery = data.priorityRegistrationCode
    ? `&c=${encodeURIComponent(data.priorityRegistrationCode)}`
    : "";
  const url = data.priorityLinkUrl || `${data.registrationUrl}?programUID=${data.programUID}${fallbackCodeQuery}`;
  navigator.clipboard.writeText(url);
  alert("Copied!");
};

onMounted(async () => {
  await getPublicWebsiteUrl();
});

const onCategoryChange = async ($event) => {
  searchByTerm.value = undefined;
  reloadData();
};

const onProgramTermChange = async ($event) => {
  reloadData();
};

const toZonedTime = (timeUTCString: string) => {
  const utcDate = new Date(timeUTCString);
  const zonedDate = utcToZonedTime(utcDate, setting.value.localTimeZone);
 
  return format(zonedDate, "dd MMM yyyy");
};

watch(
  [
    searchByProgramName,
    searchByDate,
    selectedVenueId,
    searchByDateRadio
  ],
  async () => {
    serverOptions.value.page = 1;
    await loadData();
  }
);

const reloadData = async () => {
    serverOptions.value.page = 1;
    await loadData();
};

watch(
  [
    serverOptions,
  ],
  async () => {
    await loadData();
  }
);
</script>

<style scoped>
label.form-label {
  font-size: 13px;
}

.data-table {
  caret-color: transparent;
  background: transparent;
}

.pa-4 {
  padding: 30px;
}

.vue3-easy-data-table__main {
  background: transparent !important;
}

.data-table .render-white-space {
  white-space: pre-line;
}
</style>

<style>
.data-table .sortable.asc .sortType-icon {
  opacity: 1 !important;
}

.data-table .sortable.none .sortType-icon {
  opacity: 0.3 !important;
}

.search-by-date {
  font-size: 13px !important;
}
</style>
